import {Component, EventEmitter, Input, Output} from '@angular/core';

import {FormsModule} from '@angular/forms';

@Component({
  selector: 'app-video-controls',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './video-controls.component.html',
  styleUrls: ['./video-controls.component.scss']
})
export class VideoControlsComponent {
  @Input() progressPercentage: number = 0;
  @Input() isPaused: boolean = true;
  @Input() isMuted: boolean = false;
  @Input() currentTime: string = '0:00';
  @Input() duration: string = '0:00';
  @Input() visible: boolean = true;
  @Input() mobileVisible: boolean = false; // Mobile control visibility
  @Input() volume: number = 0.2; // Volume level from parent

  @Output() progressClick = new EventEmitter<MouseEvent>();
  @Output() togglePlayPause = new EventEmitter<void>();
  @Output() toggleMute = new EventEmitter<void>();
  @Output() volumeChange = new EventEmitter<number>();
  @Output() controlsTouch = new EventEmitter<void>();

  // Track touch state to prevent gesture interference
  private touchStartTime: number = 0;
  private touchStartX: number = 0;

  // Get the display volume (0 when muted, actual volume when not muted)
  get displayVolume(): number {
    return this.isMuted ? 0 : this.volume;
  }

  // Check if the current device is mobile
  isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  // Determine if controls should be visible based on device type
  shouldShowControls(): boolean {
    const isMobile = this.isMobile();
    const mobileVisible = this.mobileVisible;
    const visible = this.visible;

    return isMobile ? mobileVisible : visible;
  }

  // Handle touch events on controls
  onControlsTouch(event: TouchEvent | MouseEvent): void {
    // Prevent event bubbling to avoid triggering video touch
    event.stopPropagation();
    this.controlsTouch.emit();
  }

  onProgressContainerClick(event: MouseEvent) {
    // Stop event propagation to prevent slide navigation
    event.stopPropagation();
    event.preventDefault();
    this.progressClick.emit(event);
  }

  onProgressTouchStart(event: TouchEvent) {
    // Stop event propagation to prevent gesture detection
    event.stopPropagation();
    this.touchStartTime = Date.now();
    this.touchStartX = event.touches[0]?.clientX || 0;
  }

  onProgressTouchEnd(event: TouchEvent) {
    // Stop event propagation to prevent gesture detection
    event.stopPropagation();
    event.preventDefault();

    // Only emit click if this was a tap (not a swipe)
    const touchEndTime = Date.now();
    const touchEndX = event.changedTouches[0]?.clientX || 0;
    const timeDiff = touchEndTime - this.touchStartTime;
    const xDiff = Math.abs(touchEndX - this.touchStartX);

    // Consider it a tap if time < 200ms and movement < 10px
    if (timeDiff < 200 && xDiff < 10) {
      // Create a synthetic mouse event for the progress click
      const syntheticEvent = new MouseEvent('click', {
        clientX: touchEndX,
        clientY: event.changedTouches[0]?.clientY || 0,
        bubbles: true,
        cancelable: true
      });

      // Set the currentTarget to match the touch event target
      Object.defineProperty(syntheticEvent, 'currentTarget', {
        value: event.currentTarget,
        writable: false
      });

      this.progressClick.emit(syntheticEvent);
    }
  }

  onVolumeInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const newVolume = parseFloat(input.value);
    this.volumeChange.emit(newVolume);
  }

  onVolumeSliderClick(event: MouseEvent) {
    const slider = event.currentTarget as HTMLElement;
    const rect = slider.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newVolume = Math.max(0, Math.min(1, percentage)); // Clamp between 0 and 1
    this.volumeChange.emit(newVolume);
  }
}
